import React from 'react';
import { Card, CardBody, CardTitle, Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import {
  AfiAverageGauge,
  KwaliteitstoeslagAverageGauge,
  VleesdikteAverageGauge,
} from '../statistics/StatisticsChart';
import { GewichtAverageGauge } from '../statistics/GewichtGaugeChart';
import { GewichtstoeslagAverageGauge } from '../statistics/GewichtstoeslagGaugeChart';
import { SpekdikteAverageGauge } from '../statistics/SpekdikteGaugeChart';
import { formatValue } from './barValues';
import { deliveryAverages, bpgAverages } from './periodAverages';

const RED = '#dc2626';
const BLACK = '#000';

// The statistics page's gauges, in its order, each fed by one webservice field.
const GAUGES = [
  { field: 'DEGEMMB', Gauge: AfiAverageGauge },
  { field: 'DEGEMKT', Gauge: KwaliteitstoeslagAverageGauge },
  { field: 'DEGEMVD', Gauge: VleesdikteAverageGauge },
  { field: 'DEGEMGW', Gauge: GewichtAverageGauge },
  { field: 'DEGEMGT', Gauge: GewichtstoeslagAverageGauge },
  { field: 'DEGEMSD', Gauge: SpekdikteAverageGauge },
];

const AverageLine = ({ color, label, value, decimals }) => (
  <div className='d-flex justify-content-center align-items-center'>
    <span style={{ color }}>&#9632;</span>
    <span className='ml-2'>
      {label}: <strong>{value === null ? '-' : formatValue(value, decimals)}</strong>
    </span>
  </div>
);

/**
 * Small gauges under the bar graphs: three a row next to two bar graphs, six a
 * row from 1600px next to three, two a row on a tablet. Every card stretches to
 * the tallest in its row, so a title that wraps does not make one stick out.
 */
const GAUGE_COLUMNS = { sm: '12', md: '6', lg: '4', className: 'col-xlg-2 d-flex' };

// Every title takes two lines' height, centred, so a title that wraps (such
// as "Gemiddelde gewichtstoeslag") does not push its gauge below the others.
const TITLE_STYLE = { minHeight: '2.4em', lineHeight: 1.2 };

/**
 * One gauge per metric for the whole period: the red needle is the average of
 * the supplier's deliveries shown on the page, the black one the BPG average of
 * the same weeks. `metrics` supplies each field's title and decimals.
 */
const PeriodGauges = ({ weeks, metrics }) => {
  const { t } = useTranslation();
  const fields = GAUGES.map(gauge => gauge.field);
  const red = deliveryAverages(weeks, fields);
  const black = bpgAverages(weeks, fields);

  return (
    <Row>
      {GAUGES.map(({ field, Gauge }) => {
        const metric = metrics.find(item => item.field === field);
        return (
          <Col key={field} {...GAUGE_COLUMNS}>
            <Card className='flex-fill'>
              <CardBody className='p-3 d-flex flex-column'>
                <CardTitle
                  tag='h6'
                  className='text-center mb-2 d-flex align-items-center justify-content-center'
                  style={TITLE_STYLE}
                >
                  {t(metric.labelKey)}
                </CardTitle>
                <Gauge redValue={red[field]} blackValue={black[field]} />
                {/* Pinned to the bottom so the lines align across the row */}
                <small className='text-muted d-block mt-auto pt-2'>
                  <AverageLine
                    color={RED}
                    label={t('individualBenchmarking.deliveriesLabel')}
                    value={red[field]}
                    decimals={metric.decimals}
                  />
                  <AverageLine
                    color={BLACK}
                    label={t('individualBenchmarking.averageLabel')}
                    value={black[field]}
                    decimals={metric.decimals}
                  />
                </small>
              </CardBody>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
};

export default PeriodGauges;
